// What the Plans & documents sheet offers, decided without the DOM.
//
// Two sources, and they answer to different things. A pod's DRAWINGS belong to
// the pod — its pod type's SharePoint folder, or its project's — and Odoo
// decides which. Its STANDARD WORK belongs to the station: the library links
// each ISW to the Odoo work centres it is used at, and the sheet shows the
// cards for the work centre this app is set to, with the rest of that work
// centre's department one tap further in.
//
// The station, not the operator. An ISW describes a job done at a place, and
// the device is set to the place; the person holding it may be filed
// anywhere, and covering at another station is exactly when they need that
// station's card rather than their own team's.

import type { IswRegisterRow } from '@/api/types';

/**
 * The ISWs linked to this work centre, in register order.
 *
 * Matched on the mrp.workcenter ID, never the name. The library mirrors the
 * work centres from Odoo and links cards by id; a station can be renamed, and
 * "Wall Tiling" is not a key.
 */
export function iswsForStation(rows: readonly IswRegisterRow[], workcentreId: number | null): IswRegisterRow[] {
  if (workcentreId == null) return [];
  return rows.filter((r) => (r.workcenters ?? []).some((w) => w.id === workcentreId));
}

/**
 * The ISWs used anywhere in this department, in register order.
 *
 * Read from the departments the library infers from each card's work centres
 * — a card is in a department because it is used at one of that department's
 * stations, and the library is where that is decided. Exact on the
 * hr.department id: a whole value stream is its own department, not the sum
 * of the teams inside it.
 */
export function iswsForDepartment(rows: readonly IswRegisterRow[], departmentId: number | null): IswRegisterRow[] {
  if (departmentId == null) return [];
  return rows.filter((r) => (r.departments ?? []).some((d) => d.id === departmentId));
}

/** "CEO / … / Value Stream 1 / Welding" → "Welding": Odoo's display name is the whole path. */
export const leafName = (path: string): string => path.split(' / ').pop()?.trim() ?? path;

/**
 * How loudly a drawing's revision status has to be said.
 *
 * The library is not all Approved: a scan of it found Draft and Rejected sheets
 * filed in the same folders as approved ones. An approved sheet needs no chip;
 * a rejected one must not be mistaken for something to build to; anything else
 * — Draft, or no status at all — is a warning. Reading "no status" as approved
 * would be the one mistake worth designing against.
 */
export function drawingTone(d: { approved: boolean; status: string }): 'ok' | 'warn' | 'danger' {
  if (d.approved) return 'ok';
  return /reject|superseded|void|withdrawn/i.test(d.status) ? 'danger' : 'warn';
}
